'use client';

import { getAddressLabels, type Path } from '@ahm/api-wrappers-core';
import { formatCurrency } from '@ahm/common-helpers';
import {
  Box,
  Button,
  CloseButton,
  Divider,
  Group,
  SimpleGrid,
  Stack,
  Text,
  rem,
  useMantineTheme,
} from '@mantine/core';
import { closeModal, type ContextModalProps } from '@mantine/modals';
import { useTranslations } from 'next-intl';
import { ModalName } from '..';
import ArrowDownIcon from '@/assets/icons/arrow-down-orange-icon.svg';
import InfoIcon from '@/assets/icons/info-blue-icon.svg';
import useIsMobile from '@/lib/is-mobile/use-is-mobile';
import { useBookingStore } from '@/stores/booking/booking-store-provider';

export interface PriceComparisonModalProps {
  newPath: Path[];
  newPrice: number;
  oldPrice: number;
  onConfirm: () => void;
  onCancel?: () => void;
}

export function PriceComparisonModal({
  innerProps,
}: ContextModalProps<PriceComparisonModalProps>) {
  const { newPath, newPrice, oldPrice, onConfirm, onCancel } = innerProps;
  const tCommon = useTranslations('common');
  const isMobile = useIsMobile();
  const theme = useMantineTheme();
  const { orderDetails: oldInfo } = useBookingStore((state) => state);

  const oldDropOffPath = oldInfo?.path?.[1];
  const newDropOffPath = newPath?.[1];

  const [oldDropOffFirstLabel, oldDropOffSecondLabel] = getAddressLabels(
    oldDropOffPath?.address ?? ''
  );
  const [newDropOffFirstLabel, newDropOffSecondLabel] = getAddressLabels(
    newDropOffPath?.address ?? ''
  );

  const paymentMethodName = (oldInfo?.payment_details as any)?.method_title;

  const handleConfirm = () => {
    onConfirm();
    closeModal(ModalName.PriceComparison);
  };

  const handleCancel = () => {
    console.log('test');
    onCancel?.();
    closeModal(ModalName.PriceComparison);
  };

  return (
    <Stack gap={0}>
      <Box pos="sticky" top={0} bg="white" style={{ zIndex: 1 }}>
        <Group justify="space-between" align="center" p={rem(16)}>
          <Text fz={rem(20)} lh={rem(24)} fw={700}>
            Phí giao hàng sẽ thay đổi
          </Text>

          <CloseButton
            pos="absolute"
            iconSize={rem(24)}
            variant="transparent"
            onClick={handleCancel}
            c="neutral.6"
            top={rem(16)}
            style={
              isMobile
                ? {
                    left: rem(16),
                    outline: 'none',
                  }
                : {
                    right: rem(16),
                    outline: 'none',
                  }
            }
          />
        </Group>
      </Box>
      <Divider />

      <Stack p={rem(16)} gap={rem(16)}>
        <Stack
          gap={0}
          p={0}
          style={{
            border: `1px solid ${theme.colors.neutral[2]}`,
            borderRadius: rem(8),
          }}
        >
          {/* old drop off: get from oldInfo.path */}
          <Group
            justify="space-between"
            align="flex-start"
            p={rem(16)}
            wrap="nowrap"
          >
            <Stack gap={rem(4)}>
              <Text
                fw={700}
                c="neutral.7"
                lineClamp={1}
                maw={rem(300)}
                truncate
              >
                {oldDropOffFirstLabel || oldDropOffPath?.address}
              </Text>
              <Text
                fw={500}
                c="neutral.5"
                lineClamp={1}
                maw={rem(300)}
                truncate
              >
                {oldDropOffSecondLabel || oldDropOffPath?.address}
              </Text>
            </Stack>

            <Text fz={rem(18)} lh={rem(24)} fw={700} c="neutral.6">
              {formatCurrency(oldPrice)}
            </Text>
          </Group>

          {/* Divider with arrow icon */}
          <Box pos="relative" w="100%" my={rem(4)}>
            <Divider
              c="neutral.2"
              style={{
                position: 'absolute',
                top: '50%',
                transform: 'translateY(-50%)',
                width: '100%',
              }}
            />
            <Box
              pos="absolute"
              left="50%"
              top="50%"
              style={{
                transform: 'translate(-50%, -50%)',
                backgroundColor: theme.white,
              }}
            >
              <ArrowDownIcon />
            </Box>
          </Box>

          {/* new drop off: get from newPath */}
          <Group
            justify="space-between"
            align="flex-start"
            p={rem(16)}
            wrap="nowrap"
          >
            <Stack gap={rem(4)}>
              <Text
                fw={700}
                c="neutral.7"
                lineClamp={1}
                maw={rem(250)}
                truncate
              >
                {newDropOffFirstLabel || newDropOffPath?.address}
              </Text>
              <Text
                fw={500}
                c="neutral.5"
                lineClamp={1}
                maw={rem(250)}
                truncate
              >
                {newDropOffSecondLabel || newDropOffPath?.address}
              </Text>
            </Stack>

            <Text fz={rem(18)} lh={rem(24)} fw={700} c="primary.6">
              {formatCurrency(newPrice)}
            </Text>
          </Group>
        </Stack>

        <Stack gap={rem(8)}>
          <Text size="md" fw={700} c="neutral.6">
            Phí thanh toán mới
          </Text>

          <Group justify="space-between" align="center">
            <Group align="center" gap={8}>
              {(oldInfo?.payment_details as any)?.icon_url ? (
                <img
                  src={(oldInfo?.payment_details as any).icon_url}
                  alt="Payment method"
                  style={{
                    width: 20,
                    height: 20,
                    objectFit: 'contain',
                  }}
                />
              ) : null}
              <Text size="md" fw={600} c="neutral.7">
                {paymentMethodName}
              </Text>
            </Group>
            <Text fz={rem(24)} lh={rem(32)} fw={700} c="primary.6">
              {formatCurrency(newPrice)}
            </Text>
          </Group>

          <Group
            gap={rem(8)}
            p={rem(12)}
            bg="blue.0"
            wrap="nowrap"
            align="flex-start"
            style={{
              borderRadius: rem(8),
            }}
          >
            <InfoIcon />
            <Text c="neutral.6" fw={500} flex={1}>
              Bạn sẽ được{' '}
              <Box component="span" c="blue.5" fw={600}>
                hoàn lại {formatCurrency(oldPrice)}{' '}
              </Box>
              sau khi xác nhận thanh toán phí mới.
            </Text>
          </Group>
        </Stack>
      </Stack>

      <SimpleGrid
        cols={isMobile ? 1 : 2}
        pos="sticky"
        bottom={0}
        bg="white"
        p={rem(24)}
        spacing={rem(16)}
        style={{
          zIndex: 1,
          boxShadow: '0 -2px 4px 0 rgba(0, 0, 0, 0.10)',
        }}
      >
        {!isMobile ? (
          <Button
            variant="filled"
            h={rem(48)}
            c="neutral.7"
            fw={700}
            fz={rem(16)}
            bg="neutral.2"
            onClick={handleCancel}
          >
            {tCommon('actions.cancel')}
          </Button>
        ) : null}

        <Button
          type="submit"
          fullWidth
          h={rem(48)}
          fw={700}
          fz={rem(16)}
          onClick={handleConfirm}
        >
          {tCommon('actions.confirm')}
        </Button>
      </SimpleGrid>
    </Stack>
  );
}
